import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { motion } from "framer-motion";

const AdminLogin = () => {
  const { setAdminToken } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Gate guard: without a valid gate token, redirect to the hidden gate page
  useEffect(() => {
    const gateToken = localStorage.getItem("rieave.gate.token");
    if (gateToken) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/gate/info");
        navigate(`/atelier-entry/${data.slug}`, { replace: true });
      } catch {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      setAdminToken(data.access_token, data.user);
      navigate("/admin");
      toast.success("Welcome back, admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden"
      style={{ background: "radial-gradient(1200px 700px at 50% 40%, #1f1520 0%, #0F0812 60%, #000 100%)" }}
      data-testid="admin-login-page"
    >
      <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full opacity-30 blur-3xl" style={{ background: "radial-gradient(circle, #C9789A, transparent 70%)" }} />
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle, #9B8FBF, transparent 70%)" }} />

      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-[28px] p-[1.5px]"
        style={{ background: "linear-gradient(135deg, rgba(201,120,154,0.75), rgba(212,184,150,0.35), rgba(155,143,191,0.75))" }}
      >
        <div className="rounded-[27px] bg-[#120A14]/90 backdrop-blur p-8 text-cream">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5" style={{ background: "linear-gradient(135deg, #C9789A, #9B8FBF)" }}>
            <Lock size={18} className="text-cream" />
          </div>
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-rose/80 mb-2">Admin · sign in</p>
          <h1 className="font-hero text-3xl mb-6 text-cream">Welcome to the <span className="serif-italic" style={{ color: "#E8B8CE" }}>atelier.</span></h1>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-label text-[11px] uppercase tracking-[0.22em] text-cream/60 block mb-2">Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 rounded-xl bg-white/5 border-white/10 text-cream placeholder:text-cream/60" data-testid="admin-email-input" />
            </div>
            <div>
              <label className="font-label text-[11px] uppercase tracking-[0.22em] text-cream/60 block mb-2">Password</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 rounded-xl bg-white/5 border-white/10 text-cream placeholder:text-cream/60" data-testid="admin-password-input" />
            </div>
            <button
              type="submit" disabled={loading}
              className="w-full h-12 rounded-xl font-label text-[13px] uppercase tracking-[0.22em] text-cream transition-all"
              style={{ background: "linear-gradient(135deg, #C9789A, #9B8FBF)" }}
              data-testid="admin-signin-button"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
